import { Switch } from '@mantine/core';
import c from 'classnames';
import { Decorator } from 'final-form';
import arrayMutators, { pop } from 'final-form-arrays';
import createDecorator from 'final-form-calculate';
import React, { useEffect, useState } from 'react';
import { Field, Form, FormSpy } from 'react-final-form';
import ReactModal from 'react-modal';
import { PageWizardProps } from '.';
import Toggle from '../../../common/Toggle';
import AdvancedSettingsToggle from '../../../components/AdvancedSettingsToggle';
import Button from '../../../components/Button';
import Error from '../../../components/Error';
import { FormSection, FormSectionBody, FormSectionHeader, StyledForm } from '../../../components/Form';
import AutoSave from '../../../components/FormAutoSave';
import Info from '../../../components/Info';
import Loader from '../../../components/Loader';
import Modal, { MODAL_STYLES } from '../../../components/Modal';
import Tabs from '../../../components/Tabs';
import FeatureEditor from '../../../components/features/FeatureEditor';
import { ROUTES } from '../../../routes/routes';
import { CustomProduct, Plan, PlanAddon, Product } from '../../../types/pricingPage';
import { StripeProduct } from '../../../types/stripeProduct';
import { formatFrequency, formatPrice } from '../../../utils/currency';
import { newFeature } from '../../../utils/plans';
import { isStripePrice, isSupported } from '../../../utils/products';
import { toUrlSlug } from '../../../utils/strings';
import VALIDATORS, { composeValidators } from '../../../utils/validators';
import Footer from './Footer';
import { FieldArray } from 'react-final-form-arrays';

const slugCalculator = createDecorator({
    field: /features\[\d+\]\.name/,
    updates: (value, name) => {
        const slugField = name.replace('.name', '.slug');
        const slug = toUrlSlug(value);
        return {
            [slugField]: slug,
        };
    },
});

const formatProductDisplay = (product: Product) => `${isStripePrice(product) ? product.product.name : product.name} - 
${formatPrice(product)} ${formatFrequency(product)}`;

interface Props extends PageWizardProps {
    onChangePlans: (plans: Plan[]) => Promise<void>;
}

export default function ChooseFeatures({ allProducts, selectedProducts, page, onChangePlans }: Props) {
    const [groupByProduct, setGroupByProduct] = useState(true);
    const { plans } = page;
    const products = plans.reduce<{ [key: string]: StripeProduct | CustomProduct }>((prev, plan) => {
        if (isStripePrice(plan.product)) {
            if (!prev[plan.product.product.id]) {
                prev[plan.product.product.id] = plan.product.product;
            }
        } else {
            prev[plan.product.id] = plan.product;
        }
        return prev;
    }, {});

    const handleSwitchProduct = async (currentPlan: Plan, newProduct: Product) => {
        await onChangePlans(
            plans.map((plan) =>
                plan.id === currentPlan.id
                    ? {
                          ...plan,
                          product: newProduct,
                          stripePriceId: isStripePrice(newProduct) ? newProduct.id : null,
                          stripeProductId: isStripePrice(newProduct) ? newProduct.product.id : null,
                      }
                    : plan
            )
        );
    };

    const handleChangePlan = async (newPlan: Plan) => {
        await onChangePlans(
            plans.map((plan) => {
                if (plan.id === newPlan.id) {
                    return newPlan;
                } else if (
                    groupByProduct &&
                    plan.stripeProductId &&
                    newPlan.stripeProductId &&
                    plan.stripeProductId === newPlan.stripeProductId
                ) {
                    return {
                        ...plan,
                        name: newPlan.name,
                        description: newPlan.description,
                        features: newPlan.features,
                        designSettings: { ...plan.designSettings, buttonText: newPlan.designSettings.buttonText },
                        callToActionUrl: newPlan.callToActionUrl,
                    };
                } else {
                    return plan;
                }
            })
        );
    };

    const handleCopyFeature = (feature: string, currentPlan: Plan) => {
        onChangePlans(
            plans.map((plan) => {
                if (plan.id === currentPlan.id || plan.stripeProductId !== currentPlan.stripeProductId) {
                    return plan;
                }
                const feat = newFeature(plan.features.length, feature);
                return { ...plan, features: [...plan.features, feat] };
            })
        );
    };

    const handleChangeGrouping = (event: React.ChangeEvent<HTMLInputElement>) => {
        setGroupByProduct(event.currentTarget.checked);
    };

    if (!selectedProducts) {
        return <Loader full />;
    } else {
        return (
            <>
                <div className="flex">
                    <div className="font-bold text-center">Add information for each of your pricing plans</div>
                    <div className="ml-auto">
                        <label className="text-xs text-gray-500 flex">
                            <Switch checked={groupByProduct} onChange={handleChangeGrouping} size="xs" color="blue" />
                            <span className="ml-2">Group by Product</span>
                        </label>
                    </div>
                </div>
                <Tabs
                    tabs={
                        groupByProduct
                            ? Object.values(products).map((product) => ({
                                  id: product.id,
                                  title: product.name,
                                  body: (
                                      <PlanForm
                                          allPlans={plans}
                                          item={product}
                                          allProducts={allProducts}
                                          onSwitchProduct={handleSwitchProduct}
                                          onChangePlan={handleChangePlan}
                                          onCopyFeature={handleCopyFeature}
                                      />
                                  ),
                              }))
                            : plans.map((plan) => ({
                                  id: plan.id,
                                  title: `${plan.name} ${formatPrice(plan.product)}${formatFrequency(plan.product)}`,
                                  body: (
                                      <PlanForm
                                          allPlans={plans}
                                          item={plan}
                                          allProducts={allProducts}
                                          onSwitchProduct={handleSwitchProduct}
                                          onChangePlan={handleChangePlan}
                                          onCopyFeature={handleCopyFeature}
                                      />
                                  ),
                              }))
                    }
                />

                <Footer
                    prev={{ href: ROUTES.PAGES_NEW_FLOW, disabled: false }}
                    next={{ href: ROUTES.PAGES_NEW_DESIGN, disabled: false }}
                    item={page}
                />
            </>
        );
    }
}

function PlanForm<T extends Plan | (CustomProduct | StripeProduct)>({
    onChangePlan,
    onSwitchProduct,
    onCopyFeature,
    allProducts,
    item,
    allPlans,
}: {
    onChangePlan: (plans: Plan) => Promise<void>;
    onSwitchProduct: (currentPlan: Plan, newProduct: Product) => Promise<void>;
    onCopyFeature: (feature: string, plan: Plan) => void;
    item: T;
    allPlans: Plan[];
    allProducts: Product[];
}) {
    const [showAdvanced, setShowAdvanced] = useState(false);
    const matchesCurrency = (product: Product) =>
        isStripePrice(plan?.product) && isStripePrice(product) ? product.currency === plan?.product.currency : false;

    const handleToggleShowAdvanced = () => setShowAdvanced(!showAdvanced);
    const [changeProductModalOpen, setChangeProductModalOpen] = useState(false);
    // FormAutoSave means we don't use the form submission process so store errors manually here
    const [submitError, setSubmitError] = useState<string>();

    const handleSave = async (newPlan: Plan) => {
        try {
            const newPlanWithOrder = {
                ...newPlan,
                features: newPlan.features.map((feature, index) => {
                    return feature.order === index ? feature : { ...feature, order: index };
                }),
            };

            return onChangePlan(newPlanWithOrder);
        } catch (err) {
            setSubmitError('Error Saving.');
        }
    };

    const handleClickSwitchProduct = () => {
        setChangeProductModalOpen(true);
    };

    const handleCloseSwitchProductModal = () => setChangeProductModalOpen(false);
    const plan = isPlan(item)
        ? item
        : allPlans.find((p) => (isStripePrice(p.product) ? p.product.product.id : p.product.id) === item.id);

    if (!plan) {
        console.log('Plan not found.');
        return null;
    }

    const handleSwitchProduct = (newProduct: Product) => onSwitchProduct(plan, newProduct);

    return (
        <Form<Plan>
            onSubmit={handleSave}
            initialValues={{ ...plan }}
            decorators={[slugCalculator as Decorator<Plan, Partial<Plan>>]}
            mutators={{
                ...(arrayMutators as any),
            }}
            key={plan.id}
        >
            {({ handleSubmit, form, values }) => (
                <StyledForm onSubmit={handleSubmit}>
                    <FormSection>
                        <FormSectionHeader>Name</FormSectionHeader>
                        <FormSectionBody>
                            <div className="mb-2">
                                <label className="text-sm text-gray-600">Plan Name</label>
                                <Field name="name" type="text">
                                    {(props) => (
                                        <input {...props.input} className="input w-full" placeholder="Plan Name..." />
                                    )}
                                </Field>
                            </div>
                        </FormSectionBody>
                    </FormSection>

                    <FormSection>
                        <FormSectionHeader>Description</FormSectionHeader>
                        <FormSectionBody>
                            <div className="mb-2">
                                <label className="text-sm text-gray-600">Plan Description</label>
                                <Field name="description" type="text">
                                    {(props) => (
                                        <input
                                            {...props.input}
                                            className="input w-full"
                                            placeholder="Plan Description..."
                                        />
                                    )}
                                </Field>
                            </div>
                        </FormSectionBody>
                    </FormSection>
                    <FormSection>
                        <div className="px-24 lg:px8 w-full">
                            <FeatureEditor
                                push={form.mutators.push}
                                onCopy={(feature: string) => onCopyFeature(feature, plan)}
                            />
                        </div>
                    </FormSection>
                    <FormSection>
                        <FormSectionHeader>Add-Ons</FormSectionHeader>
                        <FormSectionBody>
                            <FieldArray<PlanAddon> name="addons">
                                {({ fields }) => (
                                    <>
                                        {fields.map((name, index) => (
                                            <div className="mb-2">
                                                <label className="text-sm text-gray-600">
                                                    Add-on Product (optional)
                                                </label>
                                                <Field
                                                    name={`${name}.stripePriceId`}
                                                    component="select"
                                                    className="w-full border bg-white rounded px-3 py-2 outline-none"
                                                >
                                                    <option value="">-</option>
                                                    {allProducts
                                                        .filter(isSupported)
                                                        .filter(matchesCurrency)
                                                        .filter((product) => product.id !== plan.product.id)
                                                        .map((product) => (
                                                            <option
                                                                key={product.id}
                                                                className="py-1"
                                                                value={product.id}
                                                            >
                                                                {formatProductDisplay(product)}
                                                            </option>
                                                        ))}
                                                </Field>
                                                <div className="mb-2">
                                                    <label className="text-sm text-gray-600">Description</label>
                                                    <Field
                                                        name={`${name}.description`}
                                                        type="text"
                                                        component="input"
                                                        className="input w-full"
                                                        placeholder="Add-on description..."
                                                    ></Field>
                                                </div>
                                                <div className="mb-2">
                                                    <Field name={`${name}.adjustableQuantity`} type="checkbox">
                                                        {(props) => (
                                                            <label className="inline-flex items-center w-full">
                                                                <Toggle {...props.input} />
                                                                <span className="ml-2 text-sm text-gray-600">
                                                                    Adjustable Quantity?{' '}
                                                                    <Info info="When enabled, customers can choose the quantity of the add-on before proceeding to checkout" />
                                                                </span>
                                                            </label>
                                                        )}
                                                    </Field>
                                                </div>
                                                {(values.addons || [])[index].adjustableQuantity && (
                                                    <div className="mb-2">
                                                        <label className="text-sm text-gray-600 w-full">
                                                            Max Quantity (1-99)
                                                        </label>
                                                        <div className="w-full">
                                                            <Field
                                                                name={`${name}.maxQuantity`}
                                                                type="number"
                                                                component="input"
                                                                validate={composeValidators(
                                                                    VALIDATORS.max(99),
                                                                    VALIDATORS.min(1)
                                                                )}
                                                            >
                                                                {({ input, meta }) => (
                                                                    <>
                                                                        <input
                                                                            {...input}
                                                                            type="number"
                                                                            className="input w-24"
                                                                            placeholder="5"
                                                                        />
                                                                        {meta.error && (
                                                                            <span className="ml-2">
                                                                                <Error message={meta.error} />
                                                                            </span>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </Field>
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="mb-2 flex space-x-2">
                                                    <Button
                                                        kind="outline"
                                                        small
                                                        className="ml-auto"
                                                        onClick={() => {
                                                            fields.remove(index);
                                                        }}
                                                    >
                                                        Remove add-on
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                        <Button
                                            kind="outline"
                                            small
                                            className="ml-auto"
                                            onClick={() => {
                                                fields.push({
                                                    stripePriceId: '',
                                                    description: '',
                                                    adjustableQuantity: false,
                                                    maxQuantity: '5',
                                                });
                                            }}
                                        >
                                            Add add-on
                                        </Button>
                                    </>
                                )}
                            </FieldArray>
                        </FormSectionBody>
                    </FormSection>
                    <FormSection>
                        <FormSectionHeader>Buttons</FormSectionHeader>
                        <FormSectionBody>
                            <div className="mb-2">
                                <label className="text-sm text-gray-600">Signup Button</label>
                                <Field name="designSettings.buttonText" type="text">
                                    {(props) => (
                                        <input
                                            {...props.input}
                                            className="input w-full"
                                            placeholder="Signup Button text..."
                                        />
                                    )}
                                </Field>
                            </div>
                            <AdvancedSettingsToggle value={showAdvanced} onChange={handleToggleShowAdvanced} />
                            <div className={c({ hidden: !showAdvanced })}>
                                <div className="mb-4">
                                    <label className="text-sm text-gray-600">Signup Button Link</label>
                                    <Field
                                        name="callToActionUrl"
                                        component="input"
                                        type="url"
                                        validate={VALIDATORS.validUrlOrEmpty}
                                    >
                                        {({ input: { onChange, ...input }, meta }) => (
                                            <>
                                                <input
                                                    onChange={(e) => {
                                                        onChange(e);
                                                        if (!e.currentTarget.value) {
                                                            form.change('callToActionUrl', '');
                                                        }
                                                    }}
                                                    {...input}
                                                    placeholder="e.g. https://YOUR-APP/signup"
                                                    className="input w-full"
                                                />
                                                {meta.error && meta.touched && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600 flex">
                                        Previous Price
                                        <Info
                                            className="w-4 h-4 inline ml-1"
                                            info="This price is displayed before the actual price (struck through to show a discount). It is optional."
                                        />
                                        {isStripePrice(values.product) &&
                                            values.product.recurring?.interval !== 'month' && (
                                                <Field name="headlinePriceMonthly" type="checkbox">
                                                    {(props) => (
                                                        <div className="ml-auto flex space-x-2">
                                                            <label>Show monthly price</label>
                                                            <Toggle {...props.input} />
                                                        </div>
                                                    )}
                                                </Field>
                                            )}
                                    </label>
                                    {!values.headlinePriceMonthly && (
                                        <Field name="headlinePrice" type="number">
                                            {({ input: { onChange, ...input } }) => (
                                                <input
                                                    onChange={(e) => {
                                                        onChange(e);
                                                        if (!e.currentTarget.value) {
                                                            form.change('headlinePrice', null);
                                                        }
                                                    }}
                                                    {...input}
                                                    className="input w-full"
                                                    placeholder="999"
                                                />
                                            )}
                                        </Field>
                                    )}
                                </div>
                                <div className="mb-4">
                                    <label className="text-sm text-gray-600">
                                        Override Price{' '}
                                        <Info
                                            className="w-4 h-4 inline"
                                            info="Display a different price for this product. Be careful, this will NOT override the price in Stripe Checkout."
                                        />
                                    </label>

                                    <Field name="overridePrice" type="number">
                                        {({ input: { onChange, ...input } }) => (
                                            <input
                                                onChange={(e) => {
                                                    onChange(e);
                                                    if (!e.currentTarget.value) {
                                                        form.change('overridePrice', null);
                                                    }
                                                }}
                                                {...input}
                                                className="input w-full"
                                                placeholder="999"
                                            />
                                        )}
                                    </Field>
                                </div>
                                <div className="mb-2">
                                    <div>
                                        Need to change pricing?{' '}
                                        <Button onClick={handleClickSwitchProduct} kind="outline" small>
                                            Change Product
                                        </Button>
                                    </div>
                                    <ChangeProductModal
                                        allProducts={allProducts}
                                        currentProduct={plan.product}
                                        isOpen={changeProductModalOpen}
                                        onChange={handleSwitchProduct}
                                        onClose={handleCloseSwitchProductModal}
                                    />
                                </div>
                            </div>
                        </FormSectionBody>
                    </FormSection>
                    <div className="flex px-8">
                        <div className="mr-0 ml-auto">
                            {submitError && <Error message={submitError} />}
                            <AutoSave debounce={1000} save={handleSave} />
                        </div>
                    </div>
                </StyledForm>
            )}
        </Form>
    );
}

function isPlan(plan: Plan | (CustomProduct | StripeProduct)): plan is Plan {
    return (plan as Plan).stripeProductId !== undefined;
}

function ChangeProductModal(props: {
    isOpen: boolean;
    currentProduct: Product;
    allProducts: Product[];
    onChange: (product: Product) => void;
    onClose: () => void;
}) {
    const [selectedProduct, setSelectedProduct] = useState(props.currentProduct);
    useEffect(() => {
        setSelectedProduct(props.currentProduct);
    }, [props.currentProduct]);
    const handleChangeProduct = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedProduct(props.allProducts[event.target.selectedIndex]);
    };
    const handleSave = () => {
        props.onChange(selectedProduct);
        props.onClose();
    };

    return (
        <ReactModal isOpen={props.isOpen} style={MODAL_STYLES}>
            <Modal
                type="INFO"
                title="Change Plan Product"
                centered
                body={
                    <div className="text-left">
                        <label>Switch To</label>
                        <select
                            className="w-full border bg-white rounded px-3 py-2 outline-none"
                            onChange={handleChangeProduct}
                            defaultValue={selectedProduct.id}
                            value={selectedProduct.id}
                        >
                            {props.allProducts.filter(isSupported).map((product) => (
                                <option key={product.id} className="py-1" value={product.id}>
                                    {formatProductDisplay(product)}
                                </option>
                            ))}
                        </select>
                        <div className="text-sm">Your settings like features and button text will remain.</div>
                    </div>
                }
                confirmButtonText="Save"
                onConfirm={handleSave}
                onCancel={props.onClose}
            />
        </ReactModal>
    );
}
